/**
 * Deferred PWA install (Chrome/Edge/Android). iOS Safari has no programmatic install —
 * instructions are shown in a second step after the user taps Add to Home Screen.
 */

import { storageGet, storageSet } from './storage'

const STORAGE = {
  visitPrefix: 'swish_pwa_visit_count:',
  /** Legacy single snooze timestamp (migrated on read). */
  snoozeUntilPrefix: 'swish_pwa_install_snooze_until:',
  dismissPrefix: 'swish_pwa_install_dismiss:',
  softNudgeDismissPrefix: 'swish_pwa_soft_nudge_hide:',
} as const

const COOLDOWN_DAYS = 12
const COOLDOWN_EXTRA_SESSIONS = 5
const MS_PER_DAY = 24 * 60 * 60 * 1000

/** Non-standard iOS standalone flag. */
function iosStandalone(): boolean {
  return (
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  )
}

export function isStandalonePwa(): boolean {
  return iosStandalone()
}

export function isIosTouchDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const touchMac = ua.includes('Mac') && navigator.maxTouchPoints > 1
  return touchMac || /iPhone|iPad|iPod/i.test(ua)
}

/** Chromium deferred install prompt (subset of BeforeInstallPromptEvent). */
type InstallPromptLike = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredInstallPrompt: InstallPromptLike | null = null

export function registerPwaInstallListeners(): void {
  window.addEventListener(
    'beforeinstallprompt',
    (e: Event) => {
      e.preventDefault()
      deferredInstallPrompt = e as InstallPromptLike
    },
    { passive: false },
  )
}

export function hasDeferredInstallPrompt(): boolean {
  return deferredInstallPrompt != null
}

export async function promptWebInstall(): Promise<boolean> {
  const ev = deferredInstallPrompt
  if (!ev) return false
  await ev.prompt()
  const { outcome } = await ev.userChoice
  deferredInstallPrompt = null
  return outcome === 'accepted'
}

function visitKey(userId: string): string {
  return `${STORAGE.visitPrefix}${userId}`
}

function dismissKey(userId: string): string {
  return `${STORAGE.dismissPrefix}${userId}`
}

export type InstallDismissState = {
  snoozeUntilMs: number
  sessionsAtDismiss: number
}

function parseDismissState(userId: string): InstallDismissState | null {
  const raw = storageGet(dismissKey(userId))
  if (raw) {
    try {
      const j = JSON.parse(raw) as { snoozeUntilMs?: number; sessionsAtDismiss?: number }
      if (
        typeof j.snoozeUntilMs === 'number' &&
        Number.isFinite(j.snoozeUntilMs) &&
        typeof j.sessionsAtDismiss === 'number' &&
        Number.isFinite(j.sessionsAtDismiss)
      ) {
        return { snoozeUntilMs: j.snoozeUntilMs, sessionsAtDismiss: j.sessionsAtDismiss }
      }
    } catch {
      /* fall through */
    }
  }
  /** Migrate legacy snooze-only key. */
  const legacy = storageGet(`${STORAGE.snoozeUntilPrefix}${userId}`)
  if (!legacy || legacy.trim() === '') return null
  const ms = Number.parseInt(legacy, 10)
  if (!Number.isFinite(ms)) return null
  return { snoozeUntilMs: ms, sessionsAtDismiss: 0 }
}

/**
 * After "Not now" / backdrop — hide until cooldown days pass OR N more sessions completed.
 */
export function recordInstallDismiss(userId: string, completedSessions: number): void {
  storageSet(
    dismissKey(userId),
    JSON.stringify({
      snoozeUntilMs: Date.now() + COOLDOWN_DAYS * MS_PER_DAY,
      sessionsAtDismiss: completedSessions,
    }),
  )
  storageSet(`${STORAGE.snoozeUntilPrefix}${userId}`, '')
}

/** True while both time and session thresholds have not yet cleared the last dismiss. */
export function isInstallCooldownActive(userId: string, completedSessions: number): boolean {
  const d = parseDismissState(userId)
  if (!d) return false
  const timeOk = Date.now() >= d.snoozeUntilMs
  const sessionsOk = completedSessions >= d.sessionsAtDismiss + COOLDOWN_EXTRA_SESSIONS
  return !timeOk && !sessionsOk
}

export function getHomeVisitCount(userId: string): number {
  const raw = storageGet(visitKey(userId))
  if (!raw) return 0
  const n = Number.parseInt(raw, 10)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

export function incrementHomeVisitCount(userId: string): number {
  const next = getHomeVisitCount(userId) + 1
  storageSet(visitKey(userId), String(next))
  return next
}

/** @deprecated Use recordInstallDismiss + isInstallCooldownActive */
export function getInstallSnoozeUntilMs(userId: string): number | null {
  const d = parseDismissState(userId)
  return d ? d.snoozeUntilMs : null
}

/** @deprecated Use recordInstallDismiss */
export function snoozeInstallPrompt(userId: string, durationMs: number): void {
  storageSet(
    dismissKey(userId),
    JSON.stringify({
      snoozeUntilMs: Date.now() + durationMs,
      sessionsAtDismiss: 0,
    }),
  )
}

export function clearInstallSnooze(userId: string): void {
  storageSet(dismissKey(userId), '')
  storageSet(`${STORAGE.snoozeUntilPrefix}${userId}`, '')
}

function softNudgeDismissKey(userId: string): string {
  return `${STORAGE.softNudgeDismissPrefix}${userId}`
}

export function recordSoftInstallNudgeDismiss(userId: string): void {
  storageSet(softNudgeDismissKey(userId), '1')
}

export function isSoftInstallNudgeDismissed(userId: string): boolean {
  return storageGet(softNudgeDismissKey(userId)) === '1'
}

/**
 * Bottom-sheet install pitch **after logging a session** — no visit-count gate (still respects cooldown).
 */
export function shouldOfferSessionCompleteInstallPitch(args: {
  standalone: boolean
  completedSessions: number
  userId: string
}): boolean {
  const { standalone, completedSessions: s, userId } = args
  if (standalone) return false
  if (s < 1) return false
  if (isInstallCooldownActive(userId, s)) return false
  return true
}

/**
 * Natural install moments: ≥1 session, not still on “first touch” (single session + ≤1 home visit),
 * not in cooldown. Cooldown clears after days OR extra sessions — see `isInstallCooldownActive`.
 *
 * @deprecated Prefer {@link shouldOfferSessionCompleteInstallPitch} for modal pitches; home uses {@link shouldShowSoftInstallNudge}.
 */
export function shouldOfferInstallPitch(args: {
  standalone: boolean
  visitCount: number
  completedSessions: number
  userId: string
}): boolean {
  const { standalone, visitCount: v, completedSessions: s, userId } = args
  if (standalone) return false
  if (s < 1) return false
  if (isInstallCooldownActive(userId, s)) return false
  /** One logged session and at most one home landing — skip (first visit / early friction). */
  if (s === 1 && v <= 1) return false
  return true
}

/**
 * Very light home-strip nudge (not a modal). Session-complete sheet remains the primary install moment.
 */
export function shouldShowSoftInstallNudge(args: {
  standalone: boolean
  completedSessions: number
  userId: string
}): boolean {
  const { standalone, completedSessions: s, userId } = args
  if (standalone) return false
  if (s < 2) return false
  if (isInstallCooldownActive(userId, s)) return false
  if (isSoftInstallNudgeDismissed(userId)) return false
  return true
}
